import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Container } from "react-bootstrap";

import NoIndexSEO from "../../../components/SEO/NoIndexSEO";
import { seoConfig } from "../../../utils/seoConfig";
import { fetchTestimony } from "../../../redux/slices/testimonySlice";

import { ReactComponent as StarIcon } from "../../../assets/images/star.svg";
import { ReactComponent as StarUnfillIcon } from "../../../assets/images/star-unfill.svg";
import { ReactComponent as HalfStarIcon } from "../../../assets/images/half-star.svg";
import { ReactComponent as QuoteIcon } from "../../../assets/images/quote.svg";
import { ReactComponent as LeftArrowIcon } from "../../../assets/images/left-aero.svg";
import { ReactComponent as RightArrowIcon } from "../../../assets/images/right-aero.svg";
import { trackActivity } from "../../../redux/slices/trackActivitySlice";
import { useScreenTime } from "../../../hooks/useScreen";

const Testimonial = () => {
  const dispatch = useDispatch();

  const {
    testimonyItems,
    currentPage,
    totalPages,
    isLoading,
    isError,
    hasInitialized,
  } = useSelector((state) => state.testimony);

  const perPage = 6;
  const { start, stop } = useScreenTime();


  useEffect(() => {
    start();

    const handleBeforeUnload = () => {
      const result = stop();
      console.log("beforeunload result:", result);

      if (!result) return;

      dispatch(
        trackActivity({
          page_name: 'TESTIMONIAL',
          duration: result.durationMinutes,
        })
      );
    };


    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);

      const result = stop();
      if (!result) return;

      dispatch(
        trackActivity({
          page_name: 'TESTIMONIAL',
          duration: result.durationMinutes,
        })
      );
    };
  }, []);
  useEffect(() => {
    dispatch(fetchTestimony({ page: currentPage, length: perPage }));
  }, [dispatch, currentPage]);

  const handlePrev = () => {
    if (currentPage > 1) {
      dispatch(fetchTestimony({ page: currentPage - 1, length: perPage }));
    }
  };

  const handleNext = () => {
    if (currentPage < totalPages) {
      dispatch(fetchTestimony({ page: currentPage + 1, length: perPage }));
    }
  };

  const goToPage = (page) => {
    dispatch(fetchTestimony({ page, length: perPage }));
  };

  const timeAgo = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;

    const seconds = Math.floor(diffMs / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);
    const months = Math.floor(days / 30);
    const years = Math.floor(days / 365);

    if (years > 0) return `${years} Year${years > 1 ? "s" : ""} Ago`;
    if (months > 0) return `${months} Month${months > 1 ? "s" : ""} Ago`;
    if (days > 0) return `${days} Day${days > 1 ? "s" : ""} Ago`;
    if (hours > 0) return `${hours} Hour${hours > 1 ? "s" : ""} Ago`;
    if (minutes > 0) return `${minutes} Minute${minutes > 1 ? "s" : ""} Ago`;
    return "Just Now";
  };

  const renderStars = (rating) => {
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 >= 0.5;
    const emptyStars = 5 - fullStars - (hasHalfStar ? 1 : 0);

    return (
      <div className="stars">
        {[...Array(fullStars)].map((_, i) => (
          <StarIcon key={`full-${i}`} />
        ))}
        {hasHalfStar && <HalfStarIcon />}
        {[...Array(emptyStars)].map((_, i) => (
          <StarUnfillIcon key={`empty-${i}`} />
        ))}
      </div>
    );
  };

  if (!hasInitialized && isLoading) {
    return (
      <div className="testimonials-section py-5">
        <Container>
          <h3 className="title text-center">Loading testimonials...</h3>
        </Container>
      </div>
    );
  }

  if (hasInitialized && (isError || testimonyItems.length === 0)) {
    return (
      <div className="testimonials-section py-5">
        <Container>
          <h3 className="title text-center">
            {isError
              ? "Failed to load testimonials"
              : "No testimonials available"}
          </h3>
        </Container>
      </div>
    );
  }

  return (
    <div className="testimonials-section">
      <Container>
        <NoIndexSEO title={seoConfig.testimonial.title} />

        <div className="title-subtitle text-center">
          <h3 className="title">What Our Favorite Clients Say</h3>
          <div className="description">
            It is a long established fact that a reader will be distracted by
            the readable content of a page when looking at its layout.
          </div>
        </div>

        {isLoading && hasInitialized && (
          <div className="text-center my-4">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        )}

        <div className="testimonials-content row">
          {testimonyItems.map((item) => (
            <div key={item.id} className="testimonial-card col-md-4">
              <div className="content-wrap">
                {renderStars(item.rate)}

                <h3 className="title">{item.title}</h3>
                <p className="desc">{item.description}</p>

                <div className="bottom-row">
                  <div className="imgs">
                    <img
                      src={
                        item.profile_photo_path
                          ? `${process.env.REACT_APP_API_BASE_URL}/uploads/user_profile/${item.profile_photo_path}`
                          : `https://ui-avatars.com/api/?name=${encodeURIComponent(
                            `${item.user_id} `.trim() || "User"
                          )}&background=0195D3&color=fff&size=100&bold=true`
                      }
                      alt={`${item.user_id} `}
                      className="user-avatar"
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(
                          `${item.user_id} `.trim() || "User"
                        )}&background=0195D3&color=fff&size=100&bold=true`;
                      }}
                    />
                  </div>
                  <div className="user-content">
                    <h4 className="username">
                      {item.first_name} {item.last_name || ""}
                    </h4>
                    <span className="time">{timeAgo(item.created_at)}</span>
                  </div>
                  <div className="quote-icon">
                    <QuoteIcon />
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {totalPages > 1 && (
          <div className="pagination">
            <span
              className={currentPage === 1 ? "disabled" : "bg-transparent"}
              onClick={handlePrev}
            >
              <LeftArrowIcon />
            </span>

            {[...Array(totalPages)].map((_, i) => {
              const pageNum = i + 1;

              if (pageNum <= 2) {
                return (
                  <span
                    key={pageNum}
                    className={currentPage === pageNum ? "active" : ""}
                    onClick={() => goToPage(pageNum)}
                  >
                    {pageNum}
                  </span>
                );
              }

              if (pageNum >= totalPages - 1) {
                return (
                  <span
                    key={pageNum}
                    className={currentPage === pageNum ? "active" : ""}
                    onClick={() => goToPage(pageNum)}
                  >
                    {pageNum}
                  </span>
                );
              }

              if (pageNum >= currentPage - 1 && pageNum <= currentPage + 1) {
                return (
                  <span
                    key={pageNum}
                    className={currentPage === pageNum ? "active" : ""}
                    onClick={() => goToPage(pageNum)}
                  >
                    {pageNum}
                  </span>
                );
              }

              if (
                (pageNum === 3 && currentPage > 4) ||
                (pageNum === totalPages - 2 && currentPage < totalPages - 3)
              ) {
                return <span key={`ellipsis-${pageNum}`}>...</span>;
              }

              return null;
            })}

            <span
              className={
                currentPage === totalPages ? "disabled" : "bg-transparent"
              }
              onClick={handleNext}
            >
              <RightArrowIcon />
            </span>
          </div>
        )}
      </Container>
    </div>
  );
};

export default Testimonial;
